import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { isForceLogoutRefreshError, refreshSession } from "../utils/refreshSession";

const DEFAULT_CACHE_TTL_MS = 60 * 1000;
const fetchResponseCache = new Map();

function stableSerialize(value) {
  if (value === null || value === undefined) return "";
  if (typeof value !== "object") return String(value);
  if (Array.isArray(value)) return `[${value.map((v) => stableSerialize(v)).join(",")}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${k}:${stableSerialize(value[k])}`).join(",")}}`;
}

function buildFetchCacheKey(url, options) {
  const method = (options?.method || "GET").toUpperCase();
  const paramsPart = stableSerialize(options?.params || {});
  const dataPart = stableSerialize(options?.data || null);
  return `${method}|${url}|params:${paramsPart}|data:${dataPart}`;
}

function authErrorFromAxios(err) {
  const errorData = err.response?.data;
  const message = errorData?.message || errorData?.error || err.message;
  return {
    error: message,
    code: err.response?.status,
    errorCode: errorData?.code || null,
    errorData: errorData || null,
  };
}

function refreshFailureResult(refreshError) {
  if (isForceLogoutRefreshError(refreshError)) {
    window.location.href = "/login";
    return { error: "Authentication required", code: refreshError.response?.data?.code };
  }
  return { error: "Session refresh temporarily unavailable", code: "REFRESH_TEMPORARY_FAILURE" };
}

/**
 * Authenticated request with credentials and 401 refresh retry.
 * Use for one-off mutations (POST/PUT/DELETE) so components don't use axios directly.
 * @param {string} url
 * @param {{ method?: string, data?: any, headers?: object, params?: object }} options
 * @returns {Promise<{ data?: any, error?: string, code?: string }>}
 */
export const authenticatedRequest = async (url, options = {}) => {
  const method = options.method || "GET";
  const reqConfig = {
    url,
    method,
    data: options.data ?? null,
    headers: options.headers || {},
    withCredentials: true,
    params: options.params || {},
  };
  try {
    const response = await axios(reqConfig);
    return { data: response.data };
  } catch (err) {
    if (err.response?.status !== 401) {
      return authErrorFromAxios(err);
    }
    try {
      await refreshSession();
    } catch (refreshError) {
      return refreshFailureResult(refreshError);
    }
    try {
      const retryResponse = await axios(reqConfig);
      return { data: retryResponse.data };
    } catch (retryError) {
      // Retry failures are the real publish/save error — do not report them as
      // a session-refresh problem (that made curation publish look like a no-op).
      return authErrorFromAxios(retryError);
    }
  }
};

/**
 * @param options.params Query params. Inline `{ ... }` is safe — compared by serialized value, not reference.
 * @param options.cache Cache config. Inline `{ enabled: false }` is safe — compared by enabled/ttlMs, not reference.
 * @returns `{ data, loading, error, errorCode, errorStatus, refetch }` — `errorCode` is the
 * backend's stable `code` from the error body, `errorStatus` the HTTP status. Use them when the
 * UI has to branch on a specific failure (for example a 403 gate) instead of parsing `error`.
 */
export const useFetch = (url, options = { method: "GET", data: null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  const paramsKey = useMemo(() => stableSerialize(options.params || {}), [options.params]);
  const dataKey = useMemo(() => stableSerialize(options.data ?? null), [options.data]);
  const headersKey = useMemo(() => stableSerialize(options.headers || {}), [options.headers]);
  const cacheEnabled = Boolean(options.cache?.enabled);
  const cacheTtlMs =
    typeof options.cache?.ttlMs === "number" && options.cache.ttlMs > 0
      ? options.cache.ttlMs
      : null;

  const memoizedOptions = useMemo(
    () => ({
      method: options.method || "GET",
      data: options.data || null,
      headers: options.headers || {},
      params: options.params || {},
      cache: options.cache
        ? {
            enabled: cacheEnabled,
            ...(cacheTtlMs != null ? { ttlMs: cacheTtlMs } : {}),
          }
        : null,
    }),
    [options.method, dataKey, headersKey, paramsKey, cacheEnabled, cacheTtlMs],
  );

  const fetchData = useCallback(async (fetchOpts = {}) => {
    const { silent = false, bypassCache = false, signal } = fetchOpts;
    // Don't fetch if URL is null or undefined
    if (!url) {
      setLoading(false);
      setData(null);
      return;
    }

    const requestMethod = (memoizedOptions.method || "GET").toUpperCase();
    const cacheConfig = memoizedOptions.cache;
    const useCache = requestMethod === "GET" && Boolean(cacheConfig?.enabled);
    const cacheKey = useCache ? buildFetchCacheKey(url, memoizedOptions) : null;
    const cacheTtlMs =
      typeof cacheConfig?.ttlMs === "number" && cacheConfig.ttlMs > 0
        ? cacheConfig.ttlMs
        : DEFAULT_CACHE_TTL_MS;

    if (useCache && cacheKey && !bypassCache) {
      const cached = fetchResponseCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < cacheTtlMs) {
        setData(cached.data);
        setError(null);
        setLoading(false);
        return;
      }
    }
    
    if (!silent) setLoading(true);
    setError(null);
    setErrorCode(null);
    setErrorStatus(null);
    try {
      const response = await axios({
        url,
        method: memoizedOptions.method,
        data: memoizedOptions.data,
        headers: memoizedOptions.headers,
        withCredentials: true,
        params: memoizedOptions.params,
        signal,
      });
      setData(response.data);
      if (useCache && cacheKey) {
        fetchResponseCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      }
    } catch (err) {
      if (axios.isCancel(err) || err.code === "ERR_CANCELED") {
        return;
      }
      if (err.response?.status === 401) {
        try {
          await refreshSession();
        } catch (refreshError) {
          if (axios.isCancel(refreshError) || refreshError.code === "ERR_CANCELED") {
            return;
          }
          if (isForceLogoutRefreshError(refreshError)) {
            console.log('🚫 Refresh token expired or invalid, redirecting to login');
            window.location.href = '/login';
            setError('Authentication required');
            setErrorCode(refreshError.response?.data?.code || 'REFRESH_FAILED');
            setErrorStatus(refreshError.response?.status ?? 401);
          } else {
            console.log('⚠️ Refresh temporarily unavailable, preserving current auth state');
            setError('Session refresh temporarily unavailable');
            setErrorCode('REFRESH_TEMPORARY_FAILURE');
            setErrorStatus(refreshError.response?.status ?? null);
          }
          return;
        }
        try {
          const retryResponse = await axios({
            url,
            method: memoizedOptions.method,
            data: memoizedOptions.data,
            headers: memoizedOptions.headers,
            withCredentials: true,
            params: memoizedOptions.params,
            signal,
          });
          setData(retryResponse.data);
          if (useCache && cacheKey) {
            fetchResponseCache.set(cacheKey, { data: retryResponse.data, timestamp: Date.now() });
          }
        } catch (retryError) {
          if (axios.isCancel(retryError) || retryError.code === "ERR_CANCELED") {
            return;
          }
          setError(retryError.message);
          setErrorCode(retryError.response?.data?.code ?? null);
          setErrorStatus(retryError.response?.status ?? null);
        }
      } else {
        setError(err.message);
        setErrorCode(err.response?.data?.code ?? null);
        setErrorStatus(err.response?.status ?? null);
      }
    } finally {
      if (!silent && !signal?.aborted) setLoading(false);
    }
  }, [url, memoizedOptions]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchData]);

  const refetch = useCallback((opts = {}) => fetchData({ bypassCache: true, ...opts }), [fetchData]);
  return { data, loading, error, errorCode, errorStatus, refetch };
};
